import type { App } from 'vue'
import { r, redirect } from '@/common'
import { createRouter, createWebHistory } from 'vue-router'
import { apiStudioEnterLogin } from './modules/login/login-api.ts'
import { ensureVueKonva } from '@/init/init-vue-konva'
import { useProjectStore } from './store/project-store.ts'
import { useAuthStore } from '@/stores/auth-store'
import type { PermissionConfig } from '@/stores/permission-store'
import { usePermissionStore } from '@/stores/permission-store'
// import { shouldClearParams, clearProjectEpisodeParams } from './hooks/use-project-episode-params'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    enableMenu?: keyof PermissionConfig['menus']
    noPermission?: boolean
  }
}

/**
 * 路由一经发布，不得修改，只能新增和重定向
 */
export const publicRoutes = [
  r('/login', '登录', () => import('@/modules/login/guest-login-page.vue')),
  r('/', '首页', () => import('./modules/home/ai-home.vue')),
  // 根路由即首页，path '' 使 / 直接渲染首页
  // r('/', '首页', IndexLayout, [
  //   r('', '首页', () => import('./modules/home/index.vue')),
  // ]),
  r('/', 'AI动态漫开发平台', () => import('./layouts/admin-layout.tsx').then(m => m.AdminLayout), [
    /* ====== 前台管理 ======= */
    // redirect('', '/project'),
    r('project', '项目', () => import('./modules/ai-project/ai-project.vue')),
    r('project/episode', '剧集', () => import('./modules/ai-project/ai-espoies.vue')),
    r('agent', 'Agent', () => import('./modules/ai-agent/ai-agent.vue')),
    r('members', '人员管理', () => import('./modules/members/member.vue')),
    r('storyboard', '分镜', () => import('./modules/ai-pic/ai-pic.vue')),
    r('storyboard/repaint', '分镜-局部重绘', () => import('./modules/ai-repaint/ai-repaint.vue')),
    r('video', '视频', () => import('./modules/ai-video/ai-video.vue')),
    r('character', '资产', () => import('./modules/ai-character/ai-character.vue')),
    r('character/create', '创建资产', () => import('./modules/ai-character/ai-character-create.vue')),
    r('review-three-view', '人设审核', () => import('./modules/ai-review/review-three-view.vue')),
    r('review-storyboard', '分镜审核', () => import('./modules/ai-review/review-storyboard.vue')),
    r('review-video', '视频审核', () => import('./modules/ai-review/review-video.vue')),
    r('generations', '媒资', () => import('./modules/ai-generations/media-library-page.vue')),
    r('/403', '无权限', () => import('./modules/403.vue')),
    r('/icon', '图标预览', () => import('./modules/a-icon-preview/icon-preview.vue')),
    r('/:pathMatch(.*)*', '404', () => import('./modules/404.vue')),
  ]),
  r('/privacy', '隐私政策', () => import('./modules/privacy/privacy.vue')),
  r('/terms', '用户服务协议', () => import('./modules/terms/terms.vue')),
  r('/activity', '活动规则', () => import('./modules/activity/activity.vue')),
]

const router = createRouter({
  history: createWebHistory(),
  routes: publicRoutes,
})

/** 需要 VueKonva 的路由，进入前按需加载 */
const KONVA_ROUTE_PATHS = ['/storyboard/repaint']

// 路由权限守卫（只对默认路由配置生效）
router.beforeEach(async (to, from, next) => {
  const app = (router as unknown as { app?: App }).app
  if (app && KONVA_ROUTE_PATHS.some(p => to.path === p || to.path.startsWith(p + '/'))) {
    await ensureVueKonva(app)
  }
  // 处理 jump_token 参数
  const jumpToken = to.query?.jump_token as string | undefined
  const studioId = to.query?.studio_id as string | undefined
  if (jumpToken) {
    try {
      // 异步处理 jump_token，调用接口获取新的 token
      const res = await apiStudioEnterLogin({ jump_token: jumpToken })
      if (res.code === 200 && res.data?.token) {
        const newToken = res.data.token

        // 使用新的 store 系统
        const authStore = useAuthStore()
        const permissionStore = usePermissionStore()

        // 设置认证信息
        authStore.handleJumpToken(newToken, studioId)

        // 加载权限配置
        await permissionStore.fetchPermissions()
      }

      // 从 URL 中移除 jump_token 参数，避免重复处理
      const url = new URL(window.location.href)
      url.searchParams.delete('jump_token')
      url.searchParams.delete('studio_id')
      window.history.replaceState({}, '', url.toString())

      // 先中止当前导航，再 reload，避免 router 内部访问未完成的 navigation.payload 报错
      next(false)
      window.location.reload()
      return
    } catch (error) {
      console.error('处理 jump_token 失败:', error)
      next()
      return
    }
  }
  // 如果切换到需要清除存储的路由，清除 project_episode_params
  // if (shouldClearParams(to.path)) {
  //   clearProjectEpisodeParams()
  // }

  const authStore = useAuthStore()
  const permissionStore = usePermissionStore()

  // 如果用户已登录但权限未加载，先加载权限
  if (authStore.isAuthenticated && !permissionStore.isLoaded) {
    try {
      await permissionStore.fetchPermissions()
    } catch (error) {
      console.error('加载权限失败:', error)
      // 加载失败，允许访问，可能会在组件中再次尝试
      next()
      return
    }
  }

  // 定义需要权限检查的路由映射
  const routePermissionMap: Record<string, keyof PermissionConfig['menus']> = {
    '/project': 'project_menu',
    '/storyboard': 'pic_menu',
    '/storyboard/repaint': 'pic_menu',
    '/video': 'video_menu',
    '/character': 'resource_menu',
    '/character/create': 'resource_menu',
    '/review-three-view': 'audit_menu',
    '/review-storyboard': 'audit_menu',
    '/review-video': 'audit_menu',
    '/generations': 'media_menu',
    '/agent': 'agent_menu',
  }

  const enableMenu = routePermissionMap[to.path]

  if (enableMenu) {
    // 检查权限
    if (permissionStore.config?.menus) {
      const hasPermission = permissionStore.config.menus[enableMenu] === 1

      if (!hasPermission) {
        // 没有权限，标记为无权限
        to.meta.noPermission = true
        next()
        return
      }
    }
  }

  if (from.path !== '/' && from.path !== '/login') {
    // 定义项目列表空时，需要拦截的路由列表
    const routeProjectList: string[] = [
      '/storyboard',
      '/video',
      '/review-three-view',
      '/review-storyboard',
      '/review-video',
      '/agent',
    ]
    if (!routeProjectList.includes(to.path)) {
      return next()
    }
    const { projectList, isProjectListInitialized, fetchProjectList } = useProjectStore()
    if (!isProjectListInitialized.value) {
      await fetchProjectList()
    }
    if (!projectList.value || projectList.value.length === 0) {
      const locale = localStorage.getItem('app-locale')
      if (locale === 'zh-cn') {
        ElMessage.warning('暂无项目，请先创建项目')
      } else {
        ElMessage.warning('No projects, please create a project first')
      }
      return
    }
  }

  next()
})

export default router
